/**
 * /apply/terms — 체험단 서비스 이용약관 (참여자용 공개 페이지)
 *
 * 원문은 노션(inwv.notion.site/1bpluschool-seeding-terms)에서 그대로 옮겼다(REQ-199).
 * 문구를 바꾸면 시행일을 갱신하고, 제3조 2항대로 적용 7일 전부터 공지한다.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Article, Bullets, LegalShell, Numbered } from '@/features/apply/components/LegalDocument'

export const Route = createFileRoute('/apply/terms')({
  component: SeedingTermsPage,
})

function SeedingTermsPage() {
  return (
    <LegalShell title="이용약관" effectiveAt="2026년 3월 1일">
      <Article title="제1조 (목적)">
        <p>
          이 약관은 주식회사 인사이트웨이브(이하 "회사")가 운영하는 체험단 서비스(이하 "서비스")의 이용 조건 및
          절차, 회사와 이용자 간의 권리·의무를 규정함을 목적으로 합니다.
        </p>
      </Article>

      <Article title="제2조 (용어의 정의)">
        <Numbered
          items={[
            '"서비스"란 회사가 제공하는 체험단 모집·운영·관리 플랫폼을 말합니다.',
            '"이용자"란 서비스에 접속하여 체험단에 신청하거나 미션을 수행하는 자를 말합니다.',
            '"캠페인"이란 쇼핑몰 운영사(이하 "브랜드")가 서비스를 통해 운영하는 개별 체험단 활동을 말합니다.',
            '"미션"이란 캠페인에 선정된 이용자가 수행해야 하는 콘텐츠 작성 등의 과제를 말합니다.',
          ]}
        />
      </Article>

      <Article title="제3조 (약관의 효력 및 변경)">
        <Numbered
          items={[
            '이 약관은 서비스 내에 게시하거나 기타 방법으로 이용자에게 공지함으로써 효력이 발생합니다.',
            '회사는 관련 법령에 위배되지 않는 범위에서 약관을 변경할 수 있으며, 변경 시 적용일 7일 전부터 서비스 내에 공지합니다.',
            '변경된 약관에 동의하지 않는 이용자는 서비스 이용을 중단할 수 있습니다.',
          ]}
        />
      </Article>

      <Article title="제4조 (서비스의 내용)">
        <p>회사가 제공하는 서비스는 다음과 같습니다.</p>
        <Numbered
          items={[
            '체험단 캠페인 정보 제공 및 신청 접수',
            '참여자 선정 및 결과 안내',
            '미션 안내, 콘텐츠 제출 및 검수',
            '리워드(쿠폰, 적립금 등) 지급',
            '기타 위 서비스에 부수하는 업무',
          ]}
        />
      </Article>

      <Article title="제5조 (이용자의 의무)">
        <p>이용자는 다음 행위를 하여서는 안 됩니다.</p>
        <Numbered
          items={[
            '허위 정보를 기재하여 신청하는 행위',
            '타인의 개인정보를 도용하는 행위',
            '선정 후 정당한 사유 없이 미션을 수행하지 않는 행위',
            '미션 가이드라인에 맞지 않는 콘텐츠를 제출하는 행위',
            '서비스의 정상적인 운영을 방해하는 행위',
            '기타 관련 법령 및 본 약관에 위반되는 행위',
          ]}
        />
      </Article>

      <Article title="제6조 (캠페인 참여 및 미션 수행)">
        <Numbered
          items={[
            '이용자는 캠페인별 안내에 따라 신청하며, 선정 여부는 브랜드 또는 회사의 기준에 따라 결정됩니다.',
            '선정된 이용자는 캠페인에서 정한 기한 내에 미션을 수행해야 합니다.',
            '미션 기한을 초과하여 제출하는 경우, 지연 제출로 표시되며 브랜드의 판단에 따라 승인이 거부될 수 있습니다.',
            '제출된 콘텐츠가 가이드라인에 부합하지 않는 경우 반려될 수 있으며, 이용자는 반려 사유를 확인하고 재제출할 수 있습니다.',
          ]}
        />
      </Article>

      <Article title="제7조 (리워드)">
        <Numbered
          items={[
            '미션이 승인된 이용자에게는 캠페인에서 정한 리워드가 지급됩니다.',
            '리워드의 종류, 금액, 지급 시기는 캠페인별로 다를 수 있습니다.',
            <>
              다음의 경우 리워드 지급이 제한될 수 있습니다.
              <div className="mt-1.5">
                <Bullets
                  items={[
                    '허위 또는 부정한 방법으로 미션을 수행한 경우',
                    '미션 수행 후 콘텐츠를 임의로 삭제한 경우',
                    '기타 캠페인 운영 정책에 위반되는 경우',
                  ]}
                />
              </div>
            </>,
          ]}
        />
      </Article>

      <Article title="제8조 (콘텐츠의 권리)">
        <Numbered
          items={[
            '이용자가 미션 수행을 위해 제출한 콘텐츠(사진, 영상, 텍스트 등)의 저작권은 이용자에게 있습니다.',
            '다만, 이용자는 브랜드 및 회사가 마케팅 목적으로 해당 콘텐츠를 활용(재가공, SNS 게시 등)할 수 있도록 이용 허락에 동의한 것으로 봅니다.',
            '콘텐츠 활용 범위와 기간은 개별 캠페인 안내에 따릅니다.',
          ]}
        />
      </Article>

      <Article title="제9조 (서비스의 중단)">
        <Numbered
          items={[
            '회사는 시스템 점검, 장비 교체 등의 사유로 서비스 제공을 일시적으로 중단할 수 있습니다.',
            '천재지변 또는 이에 준하는 불가항력으로 서비스를 제공할 수 없는 경우 서비스 제공이 제한될 수 있습니다.',
          ]}
        />
      </Article>

      <Article title="제10조 (면책)">
        <Numbered
          items={[
            '회사는 이용자의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다.',
            '회사는 이용자가 제공한 정보의 정확성에 대해 보증하지 않습니다.',
            '이용자와 브랜드 간의 분쟁에 대해 회사는 중재 역할을 할 수 있으나, 직접적인 책임을 지지 않습니다.',
          ]}
        />
      </Article>

      <Article title="제11조 (분쟁 해결)">
        <Numbered
          items={[
            '서비스 이용과 관련하여 분쟁이 발생한 경우 회사와 이용자는 성실히 협의하여 해결합니다.',
            '협의가 이루어지지 않은 경우 관할 법원은 회사 소재지의 법원으로 합니다.',
          ]}
        />
      </Article>

      <Article title="부칙">
        <p>이 약관은 2026년 3월 1일부터 시행합니다.</p>
      </Article>
    </LegalShell>
  )
}
